/**
 * RetentionReadingPane - Painel de leitura do e-mail retido (Plano B, Task 8)
 *
 * Drawer lateral (light theme fixo, `Drawer` shared) com o corpo completo do
 * e-mail (`EmailHtmlFrame` sandboxed), anexos, info do contato e as ações
 * Aprovar/Bloquear. Só busca a entry completa (`GET /email-retentions/:id`)
 * e o contato quando `entryId` está presente (hook gated ao item
 * selecionado, CLAUDE.md NUNCA #14).
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import type { EmailRetentionStatus } from '@troia-v3/types';

import { Badge, Button, DeleteConfirmationModal, Drawer } from '@/shared/components/ui';
import { EmailHtmlFrame } from '@/shared/components/ui/EmailHtmlFrame';
import { Icon } from '@/shared/icons';
import { usePermissions } from '@/hooks';

import { useApproveRetention, useBlockRetention, useDiscardRetention, useRetentionContact, useRetentionEntry } from '../hooks/useRetention';
import { CustomerSelectModal } from './CustomerSelectModal';

export interface RetentionReadingPaneProps {
  entryId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const STATUS_LABEL: Record<EmailRetentionStatus, string> = {
  pending: 'Pendente',
  approved: 'Aprovado',
  blocked: 'Bloqueado',
  discarded: 'Descartado',
};

const STATUS_VARIANT: Record<EmailRetentionStatus, 'warning' | 'success' | 'destructive' | 'secondary'> = {
  pending: 'warning',
  approved: 'success',
  blocked: 'destructive',
  discarded: 'secondary',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(value: Date | string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Extrai a mensagem real do backend de um erro Axios — mesmo shape do
 * `getApiErrorMessage` de `helpdesk/tickets/utils` (duplicado aqui pra manter
 * o módulo `retention` autocontido, sem import cross-module de outro
 * submódulo do helpdesk).
 */
function extractErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error) && error.response?.data) {
    const data = error.response.data as Record<string, unknown>;
    if (typeof data.message === 'string') return data.message;
    if (typeof data.error === 'string') return data.error;
  }
  return fallback;
}

/** 409 = outro admin já decidiu esta entry (claim atômico do backend). */
function isConflictError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409;
}

/**
 * 402 (créditos insuficientes — approve debita `CRM_TICKET_OPENED`) e 403
 * (sem permissão) já são toastados pelo interceptor global de `@/lib/api`
 * ANTES do check de `_handleErrorLocally` — re-toastar aqui duplicaria o
 * feedback (fix pós-review Task 8).
 */
function isGloballyToastedError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === 402 || status === 403;
}

export const RetentionReadingPane: React.FC<RetentionReadingPaneProps> = ({ entryId, isOpen, onClose }) => {
  const navigate = useNavigate();
  const { data: entry, isLoading } = useRetentionEntry(entryId ?? undefined);
  const { data: contact } = useRetentionContact(entry?.contactId);
  const approveMutation = useApproveRetention();
  const discardMutation = useDiscardRetention();
  const blockMutation = useBlockRetention();

  // Gate de permissão (fix pós-review) — mesmo padrão de `ResolveTicketModal`:
  // approve exige `tickets.create` (cria ticket), block exige `tickets.update`
  // (espelha o permissionMiddleware do router de email-retentions). Sem a
  // permissão o botão é ESCONDIDO — o painel continua legível pra quem só lê.
  const { hasModulePermission } = usePermissions();
  const canApprove = hasModulePermission('tickets', 'create');
  const canBlock = hasModulePermission('tickets', 'update');

  // Modo do modal de cliente: 'approve' (só desta vez) ou 'trust' (confiar).
  const [customerModal, setCustomerModal] = useState<{ open: boolean; mode: 'approve' | 'trust' }>({ open: false, mode: 'approve' });
  const [confirmBlockOpen, setConfirmBlockOpen] = useState(false);

  // Aprovar (trust:false) ou Confiar (trust:true). Se o contato ainda não tem
  // cliente, abre o modal de associação; senão executa direto.
  const requestApprove = (mode: 'approve' | 'trust') => {
    if (!entry) return;
    if (contact && !contact.customerId) {
      setCustomerModal({ open: true, mode });
    } else {
      void handleApprove(mode === 'trust', null);
    }
  };

  const handleApprove = async (trustContact: boolean, customerId: string | null) => {
    if (!entry) return;
    try {
      const updated = await approveMutation.mutateAsync({ id: entry.id, trustContact, customerId });
      setCustomerModal({ open: false, mode: 'approve' });
      toast.success(
        trustContact ? 'Contato confiado — ticket criado' : 'Email aprovado — ticket criado',
        updated.ticketId
          ? {
              action: {
                label: 'Ver ticket',
                onClick: () => navigate(`/helpdesk/tickets/${updated.ticketId}`),
              },
            }
          : undefined,
      );
      onClose();
    } catch (error) {
      setCustomerModal({ open: false, mode: 'approve' });
      if (isConflictError(error)) {
        toast.info('Este e-mail já foi revisado por outro administrador.');
        onClose();
        return;
      }
      if (isGloballyToastedError(error)) return;
      toast.error(extractErrorMessage(error, 'Erro ao aprovar e-mail'));
    }
  };

  const handleDiscard = async () => {
    if (!entry) return;
    try {
      await discardMutation.mutateAsync(entry.id);
      toast.success('Email descartado');
      onClose();
    } catch (error) {
      if (isConflictError(error)) {
        toast.info('Este e-mail já foi revisado por outro administrador.');
        onClose();
        return;
      }
      if (isGloballyToastedError(error)) return;
      toast.error(extractErrorMessage(error, 'Erro ao descartar e-mail'));
    }
  };

  const handleBlock = async () => {
    if (!entry) return;
    try {
      await blockMutation.mutateAsync(entry.id);
      setConfirmBlockOpen(false);
      toast.success('Contato bloqueado — futuros e-mails serão descartados');
      onClose();
    } catch (error) {
      setConfirmBlockOpen(false);
      if (isConflictError(error)) {
        toast.info('Este e-mail já foi revisado por outro administrador.');
        onClose();
        return;
      }
      // 402/403: o interceptor global já toastou — não duplicar.
      if (isGloballyToastedError(error)) return;
      toast.error(extractErrorMessage(error, 'Erro ao bloquear contato'));
    }
  };

  const isPending = entry?.status === 'pending';

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        icon={<Icon name="mail" className="w-5 h-5" />}
        title={entry?.subject || '(Sem assunto)'}
        subtitle={entry?.from}
        footer={
          isPending && (canApprove || canBlock) ? (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {canBlock && (
                <Button
                  variant="secondary"
                  icon="x-circle"
                  onClick={() => void handleDiscard()}
                  loading={discardMutation.isPending}
                  data-testid="retention-action-discard"
                >
                  Descartar
                </Button>
              )}
              {canBlock && (
                <Button
                  variant="error"
                  icon="shield"
                  onClick={() => setConfirmBlockOpen(true)}
                  data-testid="retention-action-block"
                >
                  Bloquear
                </Button>
              )}
              {canApprove && (
                <Button
                  variant="secondary"
                  icon="check"
                  onClick={() => requestApprove('approve')}
                  data-testid="retention-action-approve"
                >
                  Aprovar
                </Button>
              )}
              {canApprove && (
                <Button
                  variant="success"
                  icon="user-check"
                  onClick={() => requestApprove('trust')}
                  data-testid="retention-action-trust"
                >
                  Confiar
                </Button>
              )}
            </div>
          ) : undefined
        }
      >
        <div className="px-6 py-4 space-y-4" data-testid="retention-reading-pane">
          {isLoading && (
            <div className="text-sm" style={{ color: '#94a3b8' }}>
              Carregando e-mail...
            </div>
          )}

          {entry && (
            <>
              {/* Meta do e-mail */}
              <div className="rounded-lg border p-4 space-y-1.5" style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff' }}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium" style={{ color: '#94a3b8' }}>
                    Status
                  </span>
                  <Badge variant={STATUS_VARIANT[entry.status]} dot>
                    {STATUS_LABEL[entry.status]}
                  </Badge>
                </div>
                <div className="text-sm" style={{ color: '#334155' }}>
                  <span className="font-medium">De:</span> {entry.from}
                </div>
                {entry.email.to.length > 0 && (
                  <div className="text-sm" style={{ color: '#334155' }}>
                    <span className="font-medium">Para:</span> {entry.email.to.join(', ')}
                  </div>
                )}
                {entry.email.cc && entry.email.cc.length > 0 && (
                  <div className="text-sm" style={{ color: '#334155' }}>
                    <span className="font-medium">CC:</span> {entry.email.cc.join(', ')}
                  </div>
                )}
                <div className="text-xs" style={{ color: '#94a3b8' }}>
                  Recebido em {formatDateTime(entry.email.receivedAt)}
                </div>
              </div>

              {/* Info do contato */}
              {contact && (
                <div
                  className="rounded-lg border p-4"
                  style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff' }}
                  data-testid="retention-contact-info"
                >
                  <span className="text-xs font-medium block mb-1" style={{ color: '#94a3b8' }}>
                    Contato
                  </span>
                  <div className="text-sm font-medium" style={{ color: '#1e293b' }}>
                    {contact.name}
                  </div>
                  {contact.identifiers.email[0] && (
                    <div className="text-xs" style={{ color: '#64748b' }}>
                      {contact.identifiers.email[0]}
                    </div>
                  )}
                  {contact.identifiers.phone[0] && (
                    <div className="text-xs" style={{ color: '#64748b' }}>
                      {contact.identifiers.phone[0]}
                    </div>
                  )}
                </div>
              )}

              {/* Corpo do e-mail */}
              <div className="rounded-lg overflow-hidden border" style={{ borderColor: '#e5e7eb' }}>
                {entry.email.html ? (
                  <EmailHtmlFrame html={entry.email.html} />
                ) : entry.email.plainText ? (
                  <div
                    className="p-4 text-sm whitespace-pre-wrap"
                    style={{ backgroundColor: '#ffffff', color: '#1e293b' }}
                  >
                    {entry.email.plainText}
                  </div>
                ) : (
                  <div className="p-4 text-sm italic" style={{ backgroundColor: '#ffffff', color: '#94a3b8' }}>
                    Sem conteúdo disponível
                  </div>
                )}
              </div>

              {/* Anexos */}
              {entry.email.attachments.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Icon name="paperclip" className="h-3.5 w-3.5" style={{ color: '#64748b' }} />
                    <span className="text-xs font-medium" style={{ color: '#64748b' }}>
                      {entry.email.attachments.length} anexo{entry.email.attachments.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {entry.email.attachments.map((att, i) => (
                      <a
                        key={`${att.url}-${i}`}
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-colors"
                        style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#334155' }}
                        data-testid={`retention-attachment-${i}`}
                      >
                        <Icon name="download" className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#8b5cf6' }} />
                        <div className="min-w-0">
                          <div className="truncate max-w-[160px]">{att.filename || 'Arquivo'}</div>
                          {att.size != null && <div style={{ color: '#94a3b8' }}>{formatFileSize(att.size)}</div>}
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Drawer>

      {/* Bloquear — shared DeleteConfirmationModal, variant danger */}
      <DeleteConfirmationModal
        isOpen={confirmBlockOpen}
        onClose={() => setConfirmBlockOpen(false)}
        onConfirm={() => void handleBlock()}
        variant="danger"
        title="Bloquear contato"
        message="Tem certeza que deseja bloquear este contato? Futuros e-mails deste contato serão descartados."
        confirmButtonText="Bloquear"
        loadingText="Bloqueando..."
        isLoading={blockMutation.isPending}
      />

      {/* Aprovar/Confiar — modal de associação de cliente (só quando o contato
          ainda não tem cliente vinculado). Associar é opcional. */}
      <CustomerSelectModal
        isOpen={customerModal.open}
        actionLabel={customerModal.mode === 'trust' ? 'Confiar no contato' : 'Aprovar e-mail'}
        contactName={contact?.name}
        isSubmitting={approveMutation.isPending}
        onConfirm={(customerId) => void handleApprove(customerModal.mode === 'trust', customerId)}
        onClose={() => setCustomerModal({ open: false, mode: 'approve' })}
      />
    </>
  );
};

RetentionReadingPane.displayName = 'RetentionReadingPane';
